import { useContext } from 'react';
import {
    LateEarlyRetirementFactor,
    PensionCalcData,
    useGetPensionCalcData,
} from '../api/pension-robot/useGetPensionCalcData';
import { getAOWGrossYearly } from '../components/pension-robot/cocktail-filler-screen/PensionAOWSection';
import { useAccountBalancePrediction } from '../components/pension-robot/hooks/AccountBalancePredictions';
import {
    usePartnerIsRetiredAtPension,
    usePartnerOlderThanRetirementAge,
    useSelfIsRetiredAtPension,
    useSelfOlderThanRetirementAge,
} from '../components/pension-robot/hooks/usePensionDate';
import {
    DEFAULT_PENSION_AGE,
    LAST_SECTION_INDEX,
    MIN_AGE_CLIENT,
    MONTHS_IN_YEAR,
    SUMMARY_SECTION_INDEX,
} from '../config/constants';
import {
    Account,
    AccountType,
    EmploymentStatus,
    PensionContext,
    PensionParameters,
} from '../contexts/pension-robot/PensionContext';
import {
    calculatePensionLateEarlyRetirement,
    getPerMonthFactor,
    isPensionCalculatedTogether,
} from '../utils/functions';
import { useGetPerMonthFactorValues } from './useGetPerMonthFactorValues';

const positiveOrZero = (value: number) => {
    if (value > 0) return value;
    return 0;
};

const applyTaxes = (
    grossYearly: number,
    calcData: PensionCalcData | undefined,
    employmentStatus: EmploymentStatus | undefined,
    earlierWithPension: boolean,
    isSingle: boolean,
) => {
    if (calcData === undefined) return 0;

    const step1GrossIncomeTax = (grossYearly: number) => {
        const taxPercentages = earlierWithPension
            ? {
                  taxBracketPercentage1: calcData.employedTaxPercentageDisc1,
                  taxBracketPercentage2: calcData.employedTaxPercentageDisc2,
                  taxBracketPercentage3: calcData.employedTaxPercentageDisc3,
              }
            : {
                  taxBracketPercentage1: calcData.retiredTaxPercentageDisc1,
                  taxBracketPercentage2: calcData.retiredTaxPercentageDisc2,
                  taxBracketPercentage3: calcData.retiredTaxPercentageDisc3,
              };

        const taxBracket3 = positiveOrZero(grossYearly - calcData.taxBracketAmount2!);
        const taxBracket2 = positiveOrZero(grossYearly - calcData.taxBracketAmount1! - taxBracket3);
        const taxBracket1 = positiveOrZero(grossYearly - taxBracket2 - taxBracket3);

        const totalGrossIncomeTax =
            taxBracket1 * taxPercentages.taxBracketPercentage1! +
            taxBracket2 * taxPercentages.taxBracketPercentage2! +
            taxBracket3 * taxPercentages.taxBracketPercentage3!;

        return totalGrossIncomeTax;
    };

    // Algemene heffingskorting
    const step2GeneralTaxDiscount = (grossYearly: number) => {
        const taxCredit = earlierWithPension
            ? {
                  Discountpercentage: calcData.employedGeneralTaxCreditDiscountpercentage,
                  MinimumDiscountAmount: calcData.employedGeneralTaxCreditMinimumDiscountAmount,
              }
            : {
                  Discountpercentage: calcData.retiredGeneralTaxCreditDiscountpercentage,
                  MinimumDiscountAmount: calcData.retiredGeneralTaxCreditMinimumDiscountAmount,
              };

        if (grossYearly > calcData.generalTaxCreditDiscountAmountUpperLimit!) return 0;

        if (grossYearly < calcData.generalTaxCreditDiscountAmountLowerLimit!) return taxCredit.MinimumDiscountAmount!;

        const discount =
            taxCredit.MinimumDiscountAmount! -
            (grossYearly - calcData.generalTaxCreditDiscountAmountLowerLimit!) * taxCredit.Discountpercentage!;

        return discount;
    };

    const step3LabourTaxDiscount = (grossYearly: number) => {
        if (!earlierWithPension) {
            return 0;
        }

        const { discountPercentage1, discountPercentage2, discountPercentage3, discountPercentage4 } =
            earlierWithPension
                ? {
                      discountPercentage1: calcData.employedDiscountTaxBracketPercentage1!,
                      discountPercentage2: calcData.employedDiscountTaxBracketPercentage2!,
                      discountPercentage3: calcData.employedDiscountTaxBracketPercentage3!,
                      discountPercentage4: calcData.employedDiscountTaxBracketPercentage4!,
                  }
                : {
                      discountPercentage1: calcData.retiredDiscountTaxBracketPercentage1!,
                      discountPercentage2: calcData.retiredDiscountTaxBracketPercentage2!,
                      discountPercentage3: calcData.retiredDiscountTaxBracketPercentage3!,
                      discountPercentage4: calcData.retiredDiscountTaxBracketPercentage4!,
                  };

        const fullDiscountBracket1 = discountPercentage1 * calcData.employedDiscountTaxBracketAmount1!;
        const fullDiscountBracket2 =
            discountPercentage2 *
                (calcData.employedDiscountTaxBracketAmount2! - calcData.employedDiscountTaxBracketAmount1!) +
            fullDiscountBracket1;
        const fullDiscountBracket3 =
            discountPercentage3 *
                (calcData.employedDiscountTaxBracketAmount3! - calcData.employedDiscountTaxBracketAmount2!) +
            fullDiscountBracket2;

        if (grossYearly > calcData.employedDiscountTaxBracketAmount4!) return 0;

        if (grossYearly > calcData.employedDiscountTaxBracketAmount3!)
            return (
                fullDiscountBracket3 + (grossYearly - calcData.employedDiscountTaxBracketAmount3!) * discountPercentage4
            );

        if (grossYearly > calcData.employedDiscountTaxBracketAmount2!)
            return (
                fullDiscountBracket2 + (grossYearly - calcData.employedDiscountTaxBracketAmount2!) * discountPercentage3
            );

        if (grossYearly > calcData.employedDiscountTaxBracketAmount1!)
            return (
                fullDiscountBracket1 + (grossYearly - calcData.employedDiscountTaxBracketAmount1!) * discountPercentage2
            );

        return grossYearly * discountPercentage1;
    };

    // Ouderenkorting
    const step4ElderlyTaxDiscount = (grossYearly: number) => {
        if (earlierWithPension) return 0;

        const singleDiscount = isSingle ? calcData.singleElderlyDiscountAmount! : 0;

        if (grossYearly > calcData.elderlyDiscountAmountUpperLimit!) return singleDiscount;

        if (grossYearly < calcData.elderlyDiscountAmountLowerLimit!)
            return singleDiscount + calcData.maximumElderlyDiscountAmount!;

        const inbetweenAmount = calcData.elderlyDiscountAmountUpperLimit! - calcData.elderlyDiscountAmountLowerLimit!;
        const inbetweenIncome = grossYearly - calcData.elderlyDiscountAmountLowerLimit!;
        const ederlyDiscount = (1 - inbetweenIncome / inbetweenAmount) * calcData.maximumElderlyDiscountAmount!;
        return singleDiscount + ederlyDiscount;
    };

    const step5HealthInsuranceCosts = (grossYearly: number) => {
        const healthInsuranceCosts = (percentage: number) => {
            if (grossYearly > calcData.healthInsuranceUpperLimit!)
                return percentage * calcData.healthInsuranceUpperLimit!;

            return percentage * grossYearly;
        };

        if (!earlierWithPension) {
            return healthInsuranceCosts(calcData.healthInsuranceRetiredPercentage!);
        }

        if (employmentStatus === 'self-employed') {
            return healthInsuranceCosts(calcData.healthInsuranceSelfEmployedPercentage!);
        }

        return 0;
    };

    const incomeTax = step1GrossIncomeTax(grossYearly);

    const taxDiscount =
        step2GeneralTaxDiscount(grossYearly) +
        step3LabourTaxDiscount(grossYearly) +
        step4ElderlyTaxDiscount(grossYearly);

    const totalTax = positiveOrZero(incomeTax - taxDiscount);

    return grossYearly - totalTax - step5HealthInsuranceCosts(grossYearly);
};

export const useNetCalculation = (pageNum: number = 0, forcePartnerPerspective?: boolean) => {
    const grossCalculated = useCalculations();
    const {
        parameters: { personalInfo },
    } = useContext(PensionContext);
    const calcData = useGetPensionCalcData().data;
    const legalRetirementAge = calcData?.legalRetirementAge ?? DEFAULT_PENSION_AGE;
    const isSingle = personalInfo.maritalStatus === 'single';

    const selfOlderThanRetirementAge = useSelfOlderThanRetirementAge(forcePartnerPerspective);
    const partnerOlderThanRetirementAge = usePartnerOlderThanRetirementAge(forcePartnerPerspective);

    const selfIsRetiredAtPension = useSelfIsRetiredAtPension(forcePartnerPerspective);
    const partnerIsRetiredAtPension = usePartnerIsRetiredAtPension(forcePartnerPerspective);

    const shouldCalculateFullResult = [LAST_SECTION_INDEX, SUMMARY_SECTION_INDEX].includes(pageNum);

    const selfEarlierWithPension = () => {
        return personalInfo.self?.pensionAge ? personalInfo.self?.pensionAge < legalRetirementAge : false;
    };

    const getGrossYearlyVariables = (isPartner: boolean) => {
        const person = isPartner ? 'partner' : 'me';
        const grossYearlyIncome =
            grossCalculated.aowCalculated[person].yearly +
            grossCalculated.employeePensionCalculated[person].yearly +
            grossCalculated.supplementsCalculated[person].yearly;
        return {
            aowRatio: grossYearlyIncome === 0 ? 0 : grossCalculated.aowCalculated[person].yearly / grossYearlyIncome,
            employeePensionRatio:
                grossYearlyIncome === 0
                    ? 0
                    : grossCalculated.employeePensionCalculated[person].yearly / grossYearlyIncome,
            supplementRatio:
                grossYearlyIncome === 0 ? 0 : grossCalculated.supplementsCalculated[person].yearly / grossYearlyIncome,
            grossYearlyIncome,
        };
    };

    const toCents = (amount: number) => {
        return amount * 100;
    };

    const fromCents = (amount: number) => {
        return amount / 100;
    };

    let meMonthlyAow = 0;
    let meEmployeePension = 0;
    let meMonthlySupplements = 0;
    let meNetMonthlyIncome = 0;
    let meSalary = 0;
    let partnerMonthlyAow = 0;
    let partnerEmployeePension = 0;
    let partnerMonthlySupplements = 0;
    let partnerNetMonthlyIncome = 0;
    let partnerSalary = 0;

    const meGrossYearly = getGrossYearlyVariables(false);
    const meGrossAfterTaxis = applyTaxes(
        fromCents(meGrossYearly.grossYearlyIncome),
        calcData,
        personalInfo.self?.employmentStatus,
        selfEarlierWithPension(),
        isSingle,
    );

    const meBox3 =
        grossCalculated.otherIncomeCalculated.monthly +
        grossCalculated.additionalSavingOrInvestmentsCalculated.investment.monthly +
        grossCalculated.additionalSavingOrInvestmentsCalculated.saving.monthly;

    if (!selfIsRetiredAtPension && shouldCalculateFullResult) {
        meSalary = personalInfo.self.salary ?? 0;
        meNetMonthlyIncome = meBox3;

        if (selfOlderThanRetirementAge) {
            meMonthlyAow = toCents(yearlyToMonthly(meGrossAfterTaxis * meGrossYearly.aowRatio));
        }
    } else {
        meMonthlyAow = toCents(yearlyToMonthly(meGrossAfterTaxis * meGrossYearly.aowRatio));
        meEmployeePension = toCents(yearlyToMonthly(meGrossAfterTaxis * meGrossYearly.employeePensionRatio));
        meMonthlySupplements = toCents(yearlyToMonthly(meGrossAfterTaxis * meGrossYearly.supplementRatio));
        meNetMonthlyIncome = toCents(yearlyToMonthly(meGrossAfterTaxis)) + meBox3;
    }

    const partnerGrossYearly = getGrossYearlyVariables(true);
    const partnerGrossAfterTaxis =
        personalInfo.maritalStatus === 'married'
            ? applyTaxes(
                  fromCents(partnerGrossYearly.grossYearlyIncome),
                  calcData,
                  personalInfo.partner?.employmentStatus,
                  false,
                  isSingle,
              )
            : 0;

    if (!partnerIsRetiredAtPension && shouldCalculateFullResult) {
        partnerSalary = personalInfo.partner.salary ?? 0;

        if (partnerOlderThanRetirementAge) {
            partnerMonthlyAow = toCents(yearlyToMonthly(partnerGrossAfterTaxis * partnerGrossYearly.aowRatio));
        }
    } else {
        partnerMonthlyAow = toCents(yearlyToMonthly(partnerGrossAfterTaxis * partnerGrossYearly.aowRatio));
        partnerEmployeePension = toCents(
            yearlyToMonthly(partnerGrossAfterTaxis * partnerGrossYearly.employeePensionRatio),
        );
        partnerMonthlySupplements = toCents(
            yearlyToMonthly(partnerGrossAfterTaxis * partnerGrossYearly.supplementRatio),
        );
        partnerNetMonthlyIncome = toCents(yearlyToMonthly(partnerGrossAfterTaxis));
    }

    return {
        netAow: {
            me: { monthly: meMonthlyAow },
            partner: { monthly: partnerMonthlyAow },
            total: { monthly: meMonthlyAow + partnerMonthlyAow },
        },
        netEmployeePension: {
            me: { monthly: meEmployeePension },
            partner: { monthly: partnerEmployeePension },
            total: { monthly: meEmployeePension + partnerEmployeePension },
        },
        netSupplements: {
            me: { monthly: meMonthlySupplements },
            partner: { monthly: partnerMonthlySupplements },
            total: { monthly: meMonthlySupplements + partnerMonthlySupplements },
        },
        netAdditionalSavingsAndInvestments: {
            me: grossCalculated.additionalSavingOrInvestmentsCalculated,
            total: {
                monthly:
                    grossCalculated.additionalSavingOrInvestmentsCalculated.investment.monthly +
                    grossCalculated.additionalSavingOrInvestmentsCalculated.saving.monthly,
            },
        },
        netOtherIncome: {
            me: grossCalculated.otherIncomeCalculated,
            total: grossCalculated.otherIncomeCalculated,
        },
        netIncome: {
            me: { monthly: meNetMonthlyIncome + meSalary },
            partner: { monthly: partnerNetMonthlyIncome + partnerSalary },
            total: { monthly: meNetMonthlyIncome + partnerNetMonthlyIncome + meSalary + partnerSalary },
        },
        netSalary: {
            me: { monthly: meSalary },
            partner: { monthly: partnerSalary },
            total: { monthly: meSalary + partnerSalary },
        },
        isLoading: grossCalculated.isLoading,
        isError: grossCalculated.isError,
        isFetching: grossCalculated.isFetching,
    };
};

export const useCalculations = () => {
    const {
        parameters: { personalInfo, pensionInfo, supplementInfo },
    } = useContext(PensionContext);

    const retirementForecastQuery = useGetPerMonthFactorValues(personalInfo.self?.age ?? MIN_AGE_CLIENT);
    const retirementForecastQueryPartner = useGetPerMonthFactorValues(personalInfo.partner?.age ?? MIN_AGE_CLIENT);
    const perMonthFactor = getPerMonthFactor(retirementForecastQuery.data);
    const perMonthFactorPartner = getPerMonthFactor(retirementForecastQueryPartner.data);
    const pensionCalcDataQuery = useGetPensionCalcData();

    // AOW
    const calculateAOW = () => {
        const aowMe = () => aow(pensionCalcDataQuery.data, pensionInfo, personalInfo);

        const aowPartner = () => aow(pensionCalcDataQuery.data, pensionInfo, personalInfo, true);

        return {
            me: {
                yearly: aowMe(),
                monthly: yearlyToMonthly(aowMe()),
            },
            partner: {
                yearly: aowPartner(),
                monthly: yearlyToMonthly(aowPartner()),
            },
        };
    };

    // Werkgever pensioen
    const calculateEmployeePension = () => {
        const legalRetirementAge = pensionCalcDataQuery.data?.legalRetirementAge;
        const lateEarlyRetirementFactors = pensionCalcDataQuery.data?.lateEarlyRetirement?.factors;
        const employeePensionMe = () =>
            pension(
                pensionInfo,
                personalInfo,
                legalRetirementAge ?? DEFAULT_PENSION_AGE,
                lateEarlyRetirementFactors ?? [],
            );

        const employeePensionPartner = () =>
            pension(
                pensionInfo,
                personalInfo,
                legalRetirementAge ?? DEFAULT_PENSION_AGE,
                lateEarlyRetirementFactors ?? [],
                true,
            );

        return {
            me: {
                yearly: employeePensionMe(),
                monthly: yearlyToMonthly(employeePensionMe()),
            },
            partner: {
                yearly: employeePensionPartner(),
                monthly: yearlyToMonthly(employeePensionPartner()),
            },
        };
    };

    const useCalculateSupplements = () => {
        const useGetSupplements = (isPartner: boolean) => {
            const investmentsCalc = useSupplement(supplementInfo, 'investment', isPartner);
            const savingsCalc = useSupplement(supplementInfo, 'saving', isPartner);
            const investments = investmentsCalc.computationResult;
            const savings = savingsCalc.computationResult;
            return {
                computationResult: investments + savings,
                isLoading: investmentsCalc.isLoading || savingsCalc.isLoading,
                isFetching: investmentsCalc.isFetching || savingsCalc.isFetching,
                isError: investmentsCalc.isError || savingsCalc.isError,
            };
        };

        const useMe = () => {
            const supplementCalc = useGetSupplements(false);

            const acc = supplementInfo?.yearSpacePensionAccount;
            const yearSpaceCalc = useYearSpacePensionAmount({
                type: acc?.typeOfAdditionalPensionAccount,
                currentAmount: 0,
                riskProfile: acc?.riskProfileId,
                recurringPaymentAmount: (acc?.monthlyPaymentAmount ?? 0) * 100,
                recurringPaymentInterval: 'month',
            } as Account);
            return {
                amountAtPensionAge: supplementCalc.computationResult + yearSpaceCalc.computationResultAtPension,
                isLoading: supplementCalc.isLoading || yearSpaceCalc.isInvestmentLoading,
                isError: supplementCalc.isError || yearSpaceCalc.isInvestmentError,
                isFetching: supplementCalc.isFetching || yearSpaceCalc.isInvestmentFetching,
            };
        };

        const usePartner = () => {
            const supplementCalc = useGetSupplements(true);
            return {
                amountAtPensionAge: supplementCalc.computationResult,
                isLoading: supplementCalc.isLoading,
                isError: supplementCalc.isError,
                isFetching: supplementCalc.isFetching,
            };
        };

        const supplementsSelf = useMe();
        const supplementsPartner = usePartner();

        return {
            me: {
                total: supplementsSelf.amountAtPensionAge,
                yearly: (supplementsSelf.amountAtPensionAge / perMonthFactor) * MONTHS_IN_YEAR,
                monthly: supplementsSelf.amountAtPensionAge / perMonthFactor,
            },
            partner: {
                total: supplementsPartner.amountAtPensionAge,
                yearly: (supplementsPartner.amountAtPensionAge / perMonthFactorPartner) * MONTHS_IN_YEAR,
                monthly: supplementsPartner.amountAtPensionAge / perMonthFactorPartner,
            },
            isLoading: supplementsSelf.isLoading || supplementsPartner.isLoading,
            isFetching: supplementsSelf.isFetching || supplementsPartner.isFetching,
            isError: supplementsSelf.isError || supplementsPartner.isError,
        };
    };

    // Sparen en beleggen
    const useCalculateAdditionalSavingOrInvestments = () => {
        const savingsAccount = supplementInfo?.self?.additionalSavingAccount?.isUsed
            ? supplementInfo?.self?.additionalSavingAccount?.account ?? ({} as Account)
            : ({} as Account);
        const savingCalc = useAccountBalancePrediction(savingsAccount, true);

        const investmentAccount = supplementInfo?.self?.additionalInvestmentAccount?.isUsed
            ? supplementInfo?.self?.additionalInvestmentAccount?.account ?? ({} as Account)
            : ({} as Account);
        const investmentCalc = useAccountBalancePrediction(investmentAccount, true);

        const saving = savingCalc.computationResultAtPension;
        const investment = investmentCalc.computationResultAtPension;
        return {
            saving: {
                total: saving,
                yearly: (saving / perMonthFactor) * MONTHS_IN_YEAR,
                monthly: saving / perMonthFactor,
            },
            investment: {
                total: investment,
                yearly: (investment / perMonthFactor) * MONTHS_IN_YEAR,
                monthly: investment / perMonthFactor,
            },
            isLoading: savingCalc.isInvestmentLoading || investmentCalc.isInvestmentLoading,
            isFetching: savingCalc.isInvestmentFetching || investmentCalc.isInvestmentFetching,
            isError: savingCalc.isInvestmentError || investmentCalc.isInvestmentError,
        };
    };

    // Overige inkomsten
    const calculcateOtherIncome = () => {
        const totalOtherIncome = getOtherIncome(supplementInfo);
        const totalOtherIncomePerMonth = totalOtherIncome / perMonthFactor;
        const totalOtherIncomePerYear = totalOtherIncomePerMonth * MONTHS_IN_YEAR;

        return {
            total: totalOtherIncome,
            yearly: totalOtherIncomePerYear,
            monthly: totalOtherIncomePerMonth,
        };
    };

    const aowCalculated = calculateAOW();
    const employeePensionCalculated = calculateEmployeePension();
    const supplementsCalculated = useCalculateSupplements();
    const additionalSavingOrInvestmentsCalculated = useCalculateAdditionalSavingOrInvestments();
    const otherIncomeCalculated = calculcateOtherIncome();
    const isLoading = supplementsCalculated.isLoading || additionalSavingOrInvestmentsCalculated.isLoading;
    const isFetching = supplementsCalculated.isFetching || additionalSavingOrInvestmentsCalculated.isFetching;
    const isError = supplementsCalculated.isError || additionalSavingOrInvestmentsCalculated.isError;

    return {
        aowCalculated,
        employeePensionCalculated,
        supplementsCalculated,
        additionalSavingOrInvestmentsCalculated,
        otherIncomeCalculated,
        isLoading,
        isFetching,
        isError,
    };
};

function useSupplement(supplementInfo: PensionParameters['supplementInfo'], type: AccountType, isPartner: boolean) {
    const accountsTotal =
        (isPartner ? supplementInfo?.partner?.accounts : supplementInfo?.self?.accounts) ?? ([] as Account[]);

    const relevantAccounts = accountsTotal.filter((account) => account.type === type);

    // Hooks can't be called inside e.g. a map function. Also these are used for internal calculcation
    // and not directly tied to a rendering component individually. Hence we have to write them all out
    const calc0 = useAccountBalancePrediction(relevantAccounts[0] ?? ({} as Account), false, isPartner);
    const calc1 = useAccountBalancePrediction(relevantAccounts[1] ?? ({} as Account), false, isPartner);
    const calc2 = useAccountBalancePrediction(relevantAccounts[2] ?? ({} as Account), false, isPartner);
    const calc3 = useAccountBalancePrediction(relevantAccounts[3] ?? ({} as Account), false, isPartner);
    const calc4 = useAccountBalancePrediction(relevantAccounts[4] ?? ({} as Account), false, isPartner);
    const calc5 = useAccountBalancePrediction(relevantAccounts[5] ?? ({} as Account), false, isPartner);
    const calc6 = useAccountBalancePrediction(relevantAccounts[6] ?? ({} as Account), false, isPartner);
    const calc7 = useAccountBalancePrediction(relevantAccounts[7] ?? ({} as Account), false, isPartner);
    const calc8 = useAccountBalancePrediction(relevantAccounts[8] ?? ({} as Account), false, isPartner);
    const calc9 = useAccountBalancePrediction(relevantAccounts[9] ?? ({} as Account), false, isPartner);

    const hookList = [calc0, calc1, calc2, calc3, calc4, calc5, calc6, calc7, calc8, calc9];

    const res = hookList.reduce((value, accountPrediction) => value + accountPrediction.computationResultAtPension, 0);

    return {
        computationResult: res,
        isLoading: hookList.some((x) => x.isInvestmentLoading),
        isFetching: hookList.some((x) => x.isInvestmentFetching),
        isError: hookList.some((x) => x.isInvestmentError),
    };
}

function aow(
    pensionCalcData: PensionCalcData | undefined,
    pension: PensionParameters['pensionInfo'],
    info: PensionParameters['personalInfo'],
    isPartner = false,
): number {
    if (!isPensionCalculatedTogether(info) && isPartner) return 0;

    if (isPartner) {
        if (pension?.partner?.aowGrossYearly) return pension?.partner?.aowGrossYearly;
        if (pensionCalcData) return getAOWGrossYearly(pensionCalcData, true, pension?.partner?.yearsEmployedAbroad);
        return 0;
    }

    // Self is married
    if (info.maritalStatus === 'married') {
        if (pension?.self?.aowGrossYearly?.together) return pension?.self?.aowGrossYearly?.together;
        // Otherwise recalculate it
        if (pensionCalcData) return getAOWGrossYearly(pensionCalcData, true, pension?.self?.yearsEmployedAbroad);
        return 0;
    }

    // Self is single
    if (pension?.self?.aowGrossYearly?.single) return pension?.self?.aowGrossYearly?.single;

    // if not known then recalculate it for self not married
    if (pensionCalcData) return getAOWGrossYearly(pensionCalcData, false, pension?.self?.yearsEmployedAbroad);

    return 0;
}

function getOtherIncome(personalInfo: PensionParameters['supplementInfo']): number {
    if (!personalInfo) return 0;

    if (!personalInfo.otherIncomeAvailable) return 0;

    const otherIncomeTotal =
        (personalInfo.otherIncome.gift ?? 0) +
        (personalInfo.otherIncome.heritage ?? 0) +
        (personalInfo.otherIncome.other ?? 0) +
        (personalInfo.otherIncome.surplusHouse ?? 0);

    return otherIncomeTotal;
}

function pension(
    pension: PensionParameters['pensionInfo'],
    info: PensionParameters['personalInfo'],
    legalRetirementAge: number,
    lateEarlyRetirementFactors: LateEarlyRetirementFactor[],
    isPartner = false,
): number {
    if (!isPensionCalculatedTogether(info) && isPartner) {
        return 0;
    }

    let value = 0;
    if (isPartner) {
        value = calculatePensionLateEarlyRetirement(
            pension?.partner?.employeePensionGrossYearly ?? 0,
            legalRetirementAge,
            info.partner?.pensionAge ?? 0,
            lateEarlyRetirementFactors,
        );
    } else {
        value = calculatePensionLateEarlyRetirement(
            pension?.self?.employeePensionGrossYearly ?? 0,
            legalRetirementAge,
            info.self?.pensionAge ?? 0,
            lateEarlyRetirementFactors,
        );
    }

    return value;
}

function useYearSpacePensionAmount(yearSpaceAccount: Account) {
    return useAccountBalancePrediction(yearSpaceAccount, true);
}

/**
 * Changes a yearly gross value to a monthly net value
 * @param value gross value in euros
 * @param taxPercentage The percentage as number e.g. 75
 * @returns The net amount in cents
 */
function yearlyToMonthly(value: number): number {
    return value / 12;
}
